import React, { useState, useEffect } from 'react';
import {
  X,
  Megaphone,
  Sparkles,
  Check,
  Eye,
  Sliders,
  ExternalLink,
  Clock,
  Palette,
  BellRing,
} from 'lucide-react';
import { editorApi } from '../api';

export interface NoticeBannerConfig {
  enabled: boolean;
  headline: string;
  ctaText: string;
  ctaLink: string;
  style: 'primary' | 'warning' | 'gradient' | 'dark';
  dismissible: boolean;
}

export interface PopupModalConfig {
  enabled: boolean;
  heading: string;
  offerCopy: string;
  buttonText: string;
  buttonLink: string;
  delaySeconds: number;
  badge?: string;
}

export interface PromotionsConfig {
  noticeBanner: NoticeBannerConfig;
  popupModal: PopupModalConfig;
}

export const DEFAULT_PROMOTIONS_CONFIG: PromotionsConfig = {
  noticeBanner: {
    enabled: true,
    headline: '📢 Admissions Open for 2026-27 Batches! Early Bird Scholarship Test on Sunday.',
    ctaText: 'Register Now',
    ctaLink: '/home#enquiry',
    style: 'primary',
    dismissible: true,
  },
  popupModal: {
    enabled: true,
    heading: '🎉 Exclusive Scholarship Offer!',
    offerCopy:
      'Get up to 90% fee waiver in the National Talent Discovery Exam. Limited test slots available across all campuses.',
    buttonText: 'Claim Free Test Slot',
    buttonLink: '/home#enquiry',
    delaySeconds: 3,
    badge: 'Limited Time',
  },
};

export interface PromotionsManagerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  siteId?: string;
  initialSettings?: Record<string, unknown>;
  onSave?: (config: PromotionsConfig) => Promise<void> | void;
}

export function PromotionsManagerDrawer({
  isOpen,
  onClose,
  siteId,
  initialSettings,
  onSave,
}: PromotionsManagerDrawerProps): React.JSX.Element | null {
  const [activeTab, setActiveTab] = useState<'banner' | 'popup'>('banner');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Notice Banner State
  const [bannerEnabled, setBannerEnabled] = useState(DEFAULT_PROMOTIONS_CONFIG.noticeBanner.enabled);
  const [bannerHeadline, setBannerHeadline] = useState(DEFAULT_PROMOTIONS_CONFIG.noticeBanner.headline);
  const [bannerCtaText, setBannerCtaText] = useState(DEFAULT_PROMOTIONS_CONFIG.noticeBanner.ctaText);
  const [bannerCtaLink, setBannerCtaLink] = useState(DEFAULT_PROMOTIONS_CONFIG.noticeBanner.ctaLink);
  const [bannerStyle, setBannerStyle] = useState<'primary' | 'warning' | 'gradient' | 'dark'>(
    DEFAULT_PROMOTIONS_CONFIG.noticeBanner.style
  );
  const [bannerDismissible, setBannerDismissible] = useState(
    DEFAULT_PROMOTIONS_CONFIG.noticeBanner.dismissible
  );

  // Popup Modal State
  const [popupEnabled, setPopupEnabled] = useState(DEFAULT_PROMOTIONS_CONFIG.popupModal.enabled);
  const [popupHeading, setPopupHeading] = useState(DEFAULT_PROMOTIONS_CONFIG.popupModal.heading);
  const [popupOfferCopy, setPopupOfferCopy] = useState(DEFAULT_PROMOTIONS_CONFIG.popupModal.offerCopy);
  const [popupButtonText, setPopupButtonText] = useState(DEFAULT_PROMOTIONS_CONFIG.popupModal.buttonText);
  const [popupButtonLink, setPopupButtonLink] = useState(DEFAULT_PROMOTIONS_CONFIG.popupModal.buttonLink);
  const [popupDelaySeconds, setPopupDelaySeconds] = useState(
    DEFAULT_PROMOTIONS_CONFIG.popupModal.delaySeconds
  );
  const [popupBadge, setPopupBadge] = useState(
    DEFAULT_PROMOTIONS_CONFIG.popupModal.badge || 'Limited Time'
  );

  // Load from props or localStorage
  useEffect(() => {
    if (!isOpen) return;

    let config = DEFAULT_PROMOTIONS_CONFIG;
    if (initialSettings && initialSettings.promotions) {
      config = { ...DEFAULT_PROMOTIONS_CONFIG, ...(initialSettings.promotions as Partial<PromotionsConfig>) };
    } else {
      try {
        const cached = localStorage.getItem('helios_site_promotions');
        if (cached) {
          config = { ...DEFAULT_PROMOTIONS_CONFIG, ...JSON.parse(cached) };
        }
      } catch {
        // Fallback to default
      }
    }

    if (config.noticeBanner) {
      setBannerEnabled(config.noticeBanner.enabled ?? true);
      setBannerHeadline(config.noticeBanner.headline ?? DEFAULT_PROMOTIONS_CONFIG.noticeBanner.headline);
      setBannerCtaText(config.noticeBanner.ctaText ?? DEFAULT_PROMOTIONS_CONFIG.noticeBanner.ctaText);
      setBannerCtaLink(config.noticeBanner.ctaLink ?? DEFAULT_PROMOTIONS_CONFIG.noticeBanner.ctaLink);
      setBannerStyle(config.noticeBanner.style ?? DEFAULT_PROMOTIONS_CONFIG.noticeBanner.style);
      setBannerDismissible(config.noticeBanner.dismissible ?? true);
    }

    if (config.popupModal) {
      setPopupEnabled(config.popupModal.enabled ?? true);
      setPopupHeading(config.popupModal.heading ?? DEFAULT_PROMOTIONS_CONFIG.popupModal.heading);
      setPopupOfferCopy(config.popupModal.offerCopy ?? DEFAULT_PROMOTIONS_CONFIG.popupModal.offerCopy);
      setPopupButtonText(config.popupModal.buttonText ?? DEFAULT_PROMOTIONS_CONFIG.popupModal.buttonText);
      setPopupButtonLink(config.popupModal.buttonLink ?? DEFAULT_PROMOTIONS_CONFIG.popupModal.buttonLink);
      setPopupDelaySeconds(config.popupModal.delaySeconds ?? DEFAULT_PROMOTIONS_CONFIG.popupModal.delaySeconds);
      setPopupBadge(config.popupModal.badge ?? DEFAULT_PROMOTIONS_CONFIG.popupModal.badge ?? 'Limited Time');
    }
  }, [isOpen, initialSettings]);

  if (!isOpen) return null;

  const currentConfig: PromotionsConfig = {
    noticeBanner: {
      enabled: bannerEnabled,
      headline: bannerHeadline,
      ctaText: bannerCtaText,
      ctaLink: bannerCtaLink,
      style: bannerStyle,
      dismissible: bannerDismissible,
    },
    popupModal: {
      enabled: popupEnabled,
      heading: popupHeading,
      offerCopy: popupOfferCopy,
      buttonText: popupButtonText,
      buttonLink: popupButtonLink,
      delaySeconds: popupDelaySeconds,
      badge: popupBadge,
    },
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // 1. Cache to localStorage for fast edge/client rendering on /home
      localStorage.setItem('helios_site_promotions', JSON.stringify(currentConfig));
      window.dispatchEvent(new Event('helios_promotions_updated'));

      // 2. Persist to backend site settings if siteId is present
      if (siteId) {
        await editorApi.updateSite(siteId, {
          settings_json: {
            ...(initialSettings || {}),
            promotions: currentConfig,
          },
        });
      }

      // 3. Optional callback
      await onSave?.(currentConfig);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (err) {
      console.error('Failed to save promotional settings:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const getBannerStyleClasses = (style: 'primary' | 'warning' | 'gradient' | 'dark') => {
    switch (style) {
      case 'warning':
        return 'bg-amber-500 text-slate-950';
      case 'gradient':
        return 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white';
      case 'dark':
        return 'bg-slate-900 text-slate-100 border-b border-slate-800';
      case 'primary':
      default:
        return 'bg-primary text-primary-foreground';
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex justify-end">
      <div
        className="bg-card border-l border-border w-full max-w-lg h-full shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-right duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer Header */}
        <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <Megaphone className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-foreground">Promotions &amp; Announcements</h2>
              <p className="text-xs text-muted-foreground">Manage top notice banners and promotional popup modals</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg border border-border hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer min-h-[36px] min-w-[36px] flex items-center justify-center"
            title="Close Promotions Manager"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-border bg-muted/10 shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('banner')}
            className={`flex-1 py-3 px-4 text-xs font-bold flex items-center justify-center gap-2 border-b-2 transition-all cursor-pointer min-h-[44px] ${
              activeTab === 'banner'
                ? 'border-primary text-primary bg-background'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <BellRing className="h-4 w-4" />
            <span>Top Notice Banner</span>
            {bannerEnabled && <span className="h-2 w-2 rounded-full bg-emerald-500" />}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('popup')}
            className={`flex-1 py-3 px-4 text-xs font-bold flex items-center justify-center gap-2 border-b-2 transition-all cursor-pointer min-h-[44px] ${
              activeTab === 'popup'
                ? 'border-primary text-primary bg-background'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Sparkles className="h-4 w-4" />
            <span>Promotional Popup Modal</span>
            {popupEnabled && <span className="h-2 w-2 rounded-full bg-emerald-500" />}
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {activeTab === 'banner' && (
            <div className="space-y-5">
              {/* Active Toggle */}
              <div className="p-3.5 rounded-xl border border-border bg-card flex items-center justify-between">
                <div>
                  <div className="text-xs font-bold text-foreground">Notice Banner Active</div>
                  <div className="text-[11px] text-muted-foreground">
                    Display top announcement bar across the site
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bannerEnabled}
                    onChange={(e) => setBannerEnabled(e.target.checked)}
                    className="sr-only peer"
                    data-testid="banner-enabled-toggle"
                  />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
                </label>
              </div>

              {/* Live Preview Box */}
              <div className="space-y-1.5">
                <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  <Eye className="h-3.5 w-3.5" />
                  <span>Live Preview</span>
                </div>
                <div
                  className={`rounded-xl p-3 text-xs flex items-center justify-between gap-3 shadow-sm ${getBannerStyleClasses(
                    bannerStyle
                  )}`}
                >
                  <span className="font-medium truncate">{bannerHeadline || 'Your announcement headline here'}</span>
                  {bannerCtaText && (
                    <span className="shrink-0 px-2.5 py-1 rounded bg-black/20 text-[11px] font-bold underline cursor-pointer">
                      {bannerCtaText} →
                    </span>
                  )}
                </div>
              </div>

              {/* Form Controls */}
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">
                    Announcement Headline
                  </label>
                  <input
                    type="text"
                    value={bannerHeadline}
                    onChange={(e) => setBannerHeadline(e.target.value)}
                    placeholder="e.g. Admissions Open for 2026-27 Batches!"
                    className="w-full text-xs p-2.5 rounded-lg border border-input bg-background text-foreground focus:ring-1 focus:ring-primary focus:outline-none"
                    data-testid="banner-headline-input"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">
                      Action Button / CTA Text
                    </label>
                    <input
                      type="text"
                      value={bannerCtaText}
                      onChange={(e) => setBannerCtaText(e.target.value)}
                      placeholder="e.g. Register Now"
                      className="w-full text-xs p-2.5 rounded-lg border border-input bg-background text-foreground focus:ring-1 focus:ring-primary focus:outline-none"
                      data-testid="banner-cta-text-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">
                      CTA Link URL / Hash
                    </label>
                    <input
                      type="text"
                      value={bannerCtaLink}
                      onChange={(e) => setBannerCtaLink(e.target.value)}
                      placeholder="e.g. /home#enquiry"
                      className="w-full text-xs p-2.5 rounded-lg border border-input bg-background text-foreground focus:ring-1 focus:ring-primary focus:outline-none font-mono"
                      data-testid="banner-cta-link-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">
                    Banner Visual Theme
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {(['primary', 'warning', 'gradient', 'dark'] as const).map((style) => (
                      <button
                        key={style}
                        type="button"
                        onClick={() => setBannerStyle(style)}
                        className={`py-2 px-3 rounded-lg text-xs font-semibold border capitalize transition-all cursor-pointer min-h-[38px] ${
                          bannerStyle === style
                            ? 'border-primary ring-2 ring-primary/20 bg-primary/10 text-primary font-bold'
                            : 'border-border bg-card text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid={`banner-style-${style}`}
                      >
                        {style}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="banner-dismissible"
                    checked={bannerDismissible}
                    onChange={(e) => setBannerDismissible(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <label htmlFor="banner-dismissible" className="text-xs text-muted-foreground cursor-pointer">
                    Allow visitors to dismiss the notice banner for the session
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'popup' && (
            <div className="space-y-5">
              {/* Active Toggle */}
              <div className="p-3.5 rounded-xl border border-border bg-card flex items-center justify-between">
                <div>
                  <div className="text-xs font-bold text-foreground">Promotional Modal Active</div>
                  <div className="text-[11px] text-muted-foreground">
                    Display popup offer modal after visitor arrival delay
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={popupEnabled}
                    onChange={(e) => setPopupEnabled(e.target.checked)}
                    className="sr-only peer"
                    data-testid="popup-enabled-toggle"
                  />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
                </label>
              </div>

              {/* Live Preview Box */}
              <div className="space-y-1.5">
                <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  <Eye className="h-3.5 w-3.5" />
                  <span>Live Preview</span>
                </div>
                <div className="rounded-2xl border-2 border-primary/40 bg-card p-5 shadow-xl relative overflow-hidden">
                  <div className="absolute top-3 right-3 text-muted-foreground/60">
                    <X className="h-4 w-4" />
                  </div>
                  {popupBadge && (
                    <span className="inline-block text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary mb-2">
                      {popupBadge}
                    </span>
                  )}
                  <h3 className="text-base font-black text-foreground mb-1.5">{popupHeading}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-4">{popupOfferCopy}</p>
                  <button
                    type="button"
                    className="w-full py-2.5 px-4 rounded-xl bg-primary text-primary-foreground font-bold text-xs shadow-md"
                  >
                    {popupButtonText} →
                  </button>
                </div>
              </div>

              {/* Form Controls */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">
                      Offer Heading
                    </label>
                    <input
                      type="text"
                      value={popupHeading}
                      onChange={(e) => setPopupHeading(e.target.value)}
                      placeholder="e.g. Exclusive Scholarship Offer!"
                      className="w-full text-xs p-2.5 rounded-lg border border-input bg-background text-foreground focus:ring-1 focus:ring-primary focus:outline-none"
                      data-testid="popup-heading-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">
                      Urgency Badge Tag
                    </label>
                    <input
                      type="text"
                      value={popupBadge}
                      onChange={(e) => setPopupBadge(e.target.value)}
                      placeholder="e.g. Limited Time"
                      className="w-full text-xs p-2.5 rounded-lg border border-input bg-background text-foreground focus:ring-1 focus:ring-primary focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">
                    Offer Description / Copy
                  </label>
                  <textarea
                    rows={3}
                    value={popupOfferCopy}
                    onChange={(e) => setPopupOfferCopy(e.target.value)}
                    placeholder="Enter scholarship offer details or registration urgency message..."
                    className="w-full text-xs p-2.5 rounded-lg border border-input bg-background text-foreground focus:ring-1 focus:ring-primary focus:outline-none resize-none"
                    data-testid="popup-offer-copy-input"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">
                      Button Text
                    </label>
                    <input
                      type="text"
                      value={popupButtonText}
                      onChange={(e) => setPopupButtonText(e.target.value)}
                      placeholder="e.g. Claim Free Test Slot"
                      className="w-full text-xs p-2.5 rounded-lg border border-input bg-background text-foreground focus:ring-1 focus:ring-primary focus:outline-none"
                      data-testid="popup-button-text-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">
                      Button Link URL
                    </label>
                    <input
                      type="text"
                      value={popupButtonLink}
                      onChange={(e) => setPopupButtonLink(e.target.value)}
                      placeholder="e.g. /home#enquiry"
                      className="w-full text-xs p-2.5 rounded-lg border border-input bg-background text-foreground focus:ring-1 focus:ring-primary focus:outline-none font-mono"
                      data-testid="popup-button-link-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1 flex items-center justify-between">
                    <span>Display Trigger Delay</span>
                    <span className="font-mono text-primary font-bold">{popupDelaySeconds}s</span>
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={20}
                    step={1}
                    value={popupDelaySeconds}
                    onChange={(e) => setPopupDelaySeconds(parseInt(e.target.value, 10))}
                    className="w-full accent-primary cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                    <span>1 sec (Fast)</span>
                    <span>5 sec (Recommended)</span>
                    <span>20 sec (Extended)</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="p-4 border-t border-border bg-muted/20 flex items-center justify-between shrink-0">
          <div className="text-xs text-muted-foreground">
            {saveSuccess ? (
              <span className="text-emerald-600 font-bold flex items-center gap-1 animate-in fade-in">
                <Check className="h-3.5 w-3.5" /> Saved &amp; Synced!
              </span>
            ) : (
              <span>Persists to site settings</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-border hover:bg-muted text-xs font-medium cursor-pointer min-h-[44px]"
            >
              Close
            </button>
            <button
              type="button"
              disabled={isSaving}
              onClick={handleSave}
              className="px-5 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:opacity-90 transition-opacity flex items-center gap-1.5 cursor-pointer shadow-md min-h-[44px]"
              data-testid="save-promotions-btn"
            >
              <Check className="h-4 w-4" />
              <span>{isSaving ? 'Saving...' : 'Save Settings'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
